/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { useMutation } from '@tanstack/react-query';

import { PreviousConfigResponse } from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

export type GetNodesPreviousConfigMutationConfig = {
  onSuccess?: (data: PreviousConfigResponse, variables: GetNodesPreviousConfigParams) => void;
  onError?: (error: Error, variables: GetNodesPreviousConfigParams) => void;
  onSettled?: (
    data: PreviousConfigResponse | undefined,
    error: Error | null,
    variables: GetNodesPreviousConfigParams,
  ) => void;
  onMutate?: (variables: GetNodesPreviousConfigParams) => void;
};

export type GetNodesPreviousConfigParams = {
  nodeIds: string[];
};

/**
 * Hook for getting the most recent applied standard config for each node.
 *
 * Used when reverting nodes from maintenance mode back to their previous config.
 *
 * API Endpoint:
 * - POST /tenants/:tenantId/nodes/previous-config - Get previous standard config for nodes
 *
 * @param config.onSuccess - Called after successful fetch
 * @param config.onError - Called if fetch fails
 * @param config.onSettled - Called after mutation completes (success or failure)
 * @param config.onMutate - Called before mutation starts
 *
 * @returns React Query mutation result for getting previous configs
 */
export function useGetNodesPreviousConfigMutation({
  onSuccess,
  onError,
  onSettled,
  onMutate,
}: GetNodesPreviousConfigMutationConfig) {
  const tenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);

  const { mutateAsync, isPending, isError, error } = useMutation<
    PreviousConfigResponse,
    Error,
    GetNodesPreviousConfigParams
  >({
    mutationFn: async ({ nodeIds }: GetNodesPreviousConfigParams) => {
      return mgmtPlaneAPIClient.nodes.getNodesPreviousConfig({
        tenantId: tenantId!,
        requestBody: {
          nodes: nodeIds.map((nodeId) => ({ nodeId })),
        },
      });
    },
    onSuccess,
    onError,
    onSettled,
    onMutate,
  });

  return { mutateAsync, isPending, isError, error };
}
